// RecordingTimer.jsx

import React, { useEffect, useState } from 'react';

// CSS
import './RecordingTimer.css';

/**
 * Formats seconds into HH:MM:SS
 */
const formatTime = (seconds) => {
    const h = String(Math.floor(seconds / 3600)).padStart(2, '0');
    const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
    const s = String(seconds % 60).padStart(2, '0');
    return `${h}:${m}:${s}`;
};

/**
 * RecordingTimer component
 * 
 * Displays elapsed recording time and maximum allowed duration.
 * Automatically stops at max duration (60 seconds).
 * 
 * @param {boolean} isRecording - Whether recording is active.
 * @param {function} onMaxDurationReached - Callback when max duration is hit.
 *
 * @returns {ReactNode} The rendered component.
 */
const RecordingTimer = ({ isRecording, onMaxDurationReached }) => {
    const MAX_SECONDS = 60;

    // ----- STATE ----- //

    const [elapsed, setElapsed] = useState(0);

    // ----- EFFECTS ----- //

    useEffect(() => {
        let interval = null;

        if (isRecording) {
            setElapsed(0);
            interval = setInterval(() => {
                setElapsed(prev => {
                    if (prev + 1 >= MAX_SECONDS) {
                        clearInterval(interval);
                        if (onMaxDurationReached) {
                            onMaxDurationReached();
                        }
                    }
                    return prev + 1;
                });
            }, 1000);
        } else {
            clearInterval(interval);
            setElapsed(0);
        }

        return () => clearInterval(interval);
    }, [isRecording, onMaxDurationReached]);

    // ----- RENDER ----- //

    return (
        <div className="recording-timer">
            {formatTime(elapsed)} / {formatTime(MAX_SECONDS)}
        </div>
    );
};

// Dev Tools
RecordingTimer.displayName = 'RecordingTimer';

export default RecordingTimer;
